import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import qs from "qs";
import api from "@services/api";
import { useMarketplace } from "@context/MarketplaceContext";

export function useCustomersQuery(filters, page, pageSize) {
  return useQuery({
    queryKey: ["customers", "list", filters, page, pageSize],
    queryFn: async () => {
      const { data } = await api.get("/customers", {
        params: { ...filters, page, page_size: pageSize },
        paramsSerializer: p => qs.stringify(p, { indices: false, skipNulls: true })
      });
      return data;
    },
    placeholderData: keepPreviousData
  });
}

export function useCustomerQuery(id) {
  return useQuery({
    queryKey: ["customers", "detail", id],
    queryFn: async () => {
      const { data } = await api.get(`/customers/${id}`);
      return data;
    },
    enabled: !!id
  });
}

export function useCustomerGroupsQuery() {
  // Key by the active marketplace so a switch refetches and caches don't bleed
  // across marketplaces (customer groups are scoped per marketplace).
  const { activeMarketplaceId } = useMarketplace();
  return useQuery({
    queryKey: ["customers", "groups", activeMarketplaceId ?? "all"],
    queryFn: async () => {
      const { data } = await api.get("/customer-groups");
      return data;
    }
  });
}

export function useCreateCustomerMutation() {
  return useMutation({
    mutationFn: async payload => {
      const { data } = await api.post("/customers", payload);
      return data;
    }
  });
}

export function useUpdateCustomerMutation() {
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const { data } = await api.patch(`/customers/${id}`, payload);
      return data;
    }
  });
}

export function useCustomerTierQuery(id) {
  return useQuery({
    queryKey: ["customers", "tier", id],
    queryFn: async () => {
      const { data } = await api.get(`/customers/${id}/tier`);
      return data;
    },
    enabled: !!id
  });
}

// Live per-customer "Total unpaid invoices" for many customers in one call.
// staleTime 0 so it's always re-fetched fresh — accurate even if another system
// changed the orders in the DB.
export function useCustomersUnpaidBulkQuery(customerIds) {
  const key = [...new Set((customerIds || []).map(Number).filter(Boolean))].sort((a, b) => a - b).join(",");
  return useQuery({
    queryKey: ["customers", "unpaid-bulk", key],
    enabled: key.length > 0,
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async () => {
      const { data } = await api.get("/customers-unpaid-bulk", { params: { customer_ids: key } });
      return data?.data || {};
    },
  });
}

export function useCustomerUnpaidInvoicesQuery(id) {
  return useQuery({
    queryKey: ["customers", "unpaid-invoices", id],
    queryFn: async () => {
      const { data } = await api.get(`/customers/${id}/unpaid-invoices`);
      return data?.data;
    },
    enabled: !!id
  });
}

export function useCustomerHistoryQuery(id) {
  return useQuery({
    queryKey: ["customers", "history", id],
    queryFn: async () => {
      const { data } = await api.get(`/customers/${id}/history`);
      return data;
    },
    enabled: !!id
  });
}

export function useAddCustomerHistoryMutation() {
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const { data } = await api.post(`/customers/${id}/history`, payload);
      return data;
    }
  });
}

export function useArchiveCustomerHistoryMutation() {
  return useMutation({
    mutationFn: async ({ customerId, historyId }) => {
      const { data } = await api.post(`/customers/${customerId}/history/${historyId}/archive`);
      return data;
    }
  });
}

export function useCustomerBalanceQuery(id) {
  return useQuery({
    queryKey: ["customers", "balance", id],
    queryFn: async () => {
      const { data } = await api.get(`/customers/${id}/balance`);
      return data;
    },
    enabled: !!id
  });
}

export function useAddCustomerBalanceMutation() {
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const { data } = await api.post(`/customers/${id}/balance`, payload);
      return data;
    }
  });
}

